/**
 * 配图闭环(R2-T5 通宵版 → S2 正解):按 imageSuggestions 调**视觉舱既有单图通道**生成封面 + 插图。
 *
 * ═══ 通道与归属 ═══════════════════════════════════════════════════════════════
 * 走 posterService.createTask 的 FREEFORM_RAW 路(计费=视觉舱 stage,产物挂创建人归属,
 * 出现在 /works 视觉舱线 —— 引擎/契约零改)。文章 ↔ 配图的关联(S2 正解,V18):
 * 建任务带 reporterDraftId+reporterSlot **归属列**;回捞 = listTasks(reporterDraftId=)
 * 服务端「归属列优先,任务名约定兜底」。任务名约定 `官媒稿#<draftId>·<槽位>` 保留一版
 * 做存量任务回捞兜底(改了老任务就对不上,别改)。
 *
 * ═══ 槽位 ═════════════════════════════════════════════════════════════════════
 * 设计稿口径:封面 16:9 + 至多 2 张插图(1:1);建议不足 3 条就少生成,不凑数。
 */
import type { QuoteParams } from "@/entities/pricing/api/pricingService";
import { SKU, imageVariant } from "@/entities/pricing/model/priceBook";
import {
  REPORTER_CREDITS_LONGFORM,
  REPORTER_CREDITS_PER_ARTICLE,
} from "@/entities/scene-card/model/creditRates";
import type { ImageSuggestion } from "./parse";

/**
 * 配图任务的渠道 / 档位 / 张数 —— 建单请求与报价单**同一份常量**(修复波 0926 · 代理 H)。
 *
 * ★此前页面层直接写死 `provider: "OPENAI"` / `resolutionTier: "1K"` / `requestedCount: 1`,
 *   而且从没取过报价单(`poster_task_without_quote` 软着陆)。报价单参数必须与真派发同源:
 *   一边改成 2K、另一边还按 1K 开单 = 按错的档兑现。
 */
export const REPORTER_IMAGE_PROVIDER = "OPENAI" as const;
/** 1011 算法备案:租户 domesticOnly 时配图走国内渠道(豆包 Seedream,与 ai 侧映射默认一致)。 */
export const REPORTER_IMAGE_PROVIDER_DOMESTIC = "DOUBAO" as const;
export const REPORTER_IMAGE_TIER = "1K" as const;
export const REPORTER_IMAGE_COUNT = 1;

export type ReporterImageProvider =
  | typeof REPORTER_IMAGE_PROVIDER
  | typeof REPORTER_IMAGE_PROVIDER_DOMESTIC;

/** 配图渠道:建单与报价单必须同源,统一经这里取(domesticOnly → 国内渠道)。 */
export function reporterImageProvider(domesticOnly: boolean): ReporterImageProvider {
  return domesticOnly ? REPORTER_IMAGE_PROVIDER_DOMESTIC : REPORTER_IMAGE_PROVIDER;
}

/** 一个槽位出一张图的正式报价单参数(SKU = 视觉舱出图,与后端 computeRequestedCount 同口径 = 1 张)。 */
export function reporterSlotQuoteParams(
  provider: ReporterImageProvider = REPORTER_IMAGE_PROVIDER
): QuoteParams {
  return {
    skuCode: SKU.posterImage,
    variantKey: imageVariant(provider, REPORTER_IMAGE_TIER),
    qty: REPORTER_IMAGE_COUNT,
  };
}

/**
 * 结构化最小类型(FSD:features 互不 import,poster 的 DTO 结构上满足本接口 ——
 * 与 entities/work/model/mappers 的 RawPosterTask 同姿势;页面层把真 DTO 直接传进来)。
 */
export interface ReporterImageTask {
  id: number;
  name?: string | null;
  status: string;
  coverThumbUrl?: string | null;
  errorMessage?: string | null;
  /** S2 归属列(V18;存量名约定任务 null)。 */
  reporterDraftId?: number | null;
  reporterSlot?: string | null;
}

/** 槽位画幅(与海报 PosterRatio 同字面量,页面层直传 createTask)。 */
export type ReporterImageRatio = "16:9" | "1:1" | "3:4";

export interface ReporterImageSlot {
  /** 槽位 key(进任务名,回捞匹配用;改了老任务就对不上,别改)。 */
  slot: string;
  label: string;
  ratio: ReporterImageRatio;
  suggestion: ImageSuggestion;
}

export const REPORTER_IMAGE_MAX_SLOTS = 6;

const INSERT_RATIOS = new Set<ReporterImageRatio>(["1:1", "16:9", "3:4"]);

/** 建议 → 槽位:第 1 条当封面(恒 16:9),其余当插图(画幅听建议,默认 1:1),封顶 6 张(批二)。 */
export function imageSlotsOf(suggestions: ImageSuggestion[]): ReporterImageSlot[] {
  return suggestions.slice(0, REPORTER_IMAGE_MAX_SLOTS).map((s, i) => {
    if (i === 0) return { slot: "cover", label: "封面", ratio: "16:9" as const, suggestion: s };
    const ratio = INSERT_RATIOS.has(s.ratio as ReporterImageRatio)
      ? (s.ratio as ReporterImageRatio)
      : ("1:1" as const);
    return { slot: `insert${i}`, label: `插图${i}`, ratio, suggestion: s };
  });
}

export const reporterTaskPrefix = (draftId: number) => `官媒稿#${draftId}·`;

export const reporterTaskName = (draftId: number, slot: string) =>
  `${reporterTaskPrefix(draftId)}${slot}`;

/**
 * 建议 → 出图指令。★「不出现文字」必须写死:引擎会把文案画进图(踩过,必人眼过);
 * 配图是插画位,文字排版归文章本身。
 */
export function imagePromptOf(s: ImageSuggestion): string {
  const parts = [
    `画面场景:${s.scene}`,
    s.keywords.length ? `关键元素:${s.keywords.join("、")}` : "",
    `风格:${s.style || "简洁商务插画"}`,
    "画面中不要出现任何文字、字母或数字。",
  ];
  return parts.filter(Boolean).join("。");
}

/**
 * 回捞:归属列优先(reporterDraftId+reporterSlot,S2 正解)、任务名前缀兜底(存量任务)
 * 按槽位归位;同槽多次生成取**最新**(id 最大 = 换一张重 roll 的语义)。
 */
export function matchTasksToSlots<T extends ReporterImageTask>(
  tasks: T[],
  draftId: number
): Record<string, T> {
  const prefix = reporterTaskPrefix(draftId);
  const out: Record<string, T> = {};
  for (const t of tasks) {
    let slot: string | null = null;
    if (t.reporterDraftId === draftId) {
      // 归属列在场就是权威:slot 为 null = 哨兵态(已从图位移除),**不落名字兜底**
      // (否则约定名任务移除后会从名字路"复活"回槽位;与后端 ReporterMediaRef.slotOf 同口径)。
      slot = t.reporterSlot ?? null;
    } else if (t.name?.startsWith(prefix)) {
      slot = t.name.slice(prefix.length);
    }
    if (!slot) continue;
    if (!out[slot] || (t.id ?? 0) > (out[slot].id ?? 0)) out[slot] = t;
  }
  return out;
}

/**
 * 卡面口径:短文/长文档位的**下界**数(0827 重标)。
 * ★B2 #5 ③(0927)起对客展示一律走 `creditRates.reporterArticleCreditsApprox`(区间「≈ 6~12」),
 *   本函数只剩单值下界,别再拿它当「预计 N 积分」印在界面上。
 */
export function estimateCredits(targetWords: number): number {
  return targetWords > 2000 ? REPORTER_CREDITS_LONGFORM : REPORTER_CREDITS_PER_ARTICLE;
}

/** 正文字数(展示口径):与后端 isalnum 单口径同向 —— 只数字母/数字/汉字,不数空白标点。 */
export function countWords(text: string): number {
  let n = 0;
  for (const ch of text) if (/[\p{L}\p{N}]/u.test(ch)) n += 1;
  return n;
}
